import { ReactNode, useEffect } from "react";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { CustomDropdown, CustomDropdownItem } from "@/components/ui/dropdown";
import { Button } from "@/components/ui/button";
import { Squares2X2Icon, Bars3Icon, ChartBarIcon } from "@heroicons/react/24/solid";
import { ArrowUpOnSquareIcon } from "@heroicons/react/24/outline";
import { ComputerDesktopIcon, PhotoIcon, TableCellsIcon } from "@heroicons/react/20/solid";
import { useCreateImages, useCreatePowerPoint } from "@/api/studio/exports/queries";
import { useModalStore } from "@/stores/use-modal-store";
import { useSegment } from "@/hooks/use-segment";
import { SelectSortReports } from "@/components/ui/select";
import { useReportFiltersStore } from "../stores/use-report-filters-store";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import { ExportPowerPointModal } from "../modals/export-powerpoint-modal";
import { ExportVisualsModal } from "../modals/export-visuals-modal";
import { ExportDataModal } from "../modals/export-data-modal";
import { EditStudyModal } from "../modals/edit-study-modal";
import { useReportsContext, ViewType } from "../contexts/reports-context";
import { InfoModalTrigger } from "@/components/ui/modal";
import { AttentionScoreInfoModal } from "../modals/attention-score-info-modal";
import { CreativeImpactInfoModal } from "../modals/creative-impact-info-modal";
import { EmotionScoreInfoModal } from "../modals/emotion-score-info-modal";
import { MemoryScoreInfoModal } from "../modals/memory-score-info-modal";
import { useFeatureFlag } from "@/hooks/use-feature-flag";

/* Prop Types */
interface ReportNavToolbarProps {
  projectId: string;
  studyId: string;
  currentTab: "creative" | "attention" | "memory" | "emotion";
  hasContext?: boolean;
  baseUrl?: string;
  rightContent?: ReactNode;
  showFilters?: boolean;
}

/*
 * ReportNavToolbar Component
 * Displays navigation links for the report pages
 */
export const ReportNavToolbar = ({ projectId, studyId, currentTab, rightContent, showFilters = true }: ReportNavToolbarProps) => {
  /* Queries */
  const { mutateAsync: createPowerPoint, isPending: isCreatePowerPointPending } = useCreatePowerPoint();
  const { mutateAsync: createImages, isPending: isCreateImagesPending } = useCreateImages();

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const isNewExportOptionsEnabled = useFeatureFlag("newExportOptions");

  /* Actions */
  const { setSortBy, setOrderBy } = useReportFiltersStore((state) => state.reportFiltersActions);

  /* Stores */
  const { open } = useModalStore((state) => state.modalActions);
  const sortBy = useReportFiltersStore((state) => state.sortBy);
  const orderBy = useReportFiltersStore((state) => state.orderBy);

  /* Effects */
  useEffect(() => {
    if (orderBy !== "name") {
      setOrderBy(currentTab);
    }
  }, [currentTab, setOrderBy]);

  /* Context */
  const {
    state: { view },
    actions: { setView },
  } = useReportsContext();

  /* Handlers */
  const handleReportsExport = async () => {
    // track the export event
    segmentTrackEvent("Study Exported", {
      projectId,
      studyId,
      exportType: "Presentation",
      studyType: "asset",
    });
    // get the taskId before opening the modal - we disable the button if the task is pending
    // if this feels like its too much, we can move this logic to the modal itself
    const { taskId } = await createPowerPoint({ projectId, studyId, orderBy, sortBy });
    open(<ExportPowerPointModal projectId={projectId} studyId={studyId} taskId={taskId} />);
  };

  const handleVisualsExport = async () => {
    segmentTrackEvent("Study Exported", {
      projectId,
      studyId,
      exportType: "Visuals",
      studyType: "asset",
    });
    const { taskId } = await createImages({ projectId, studyId, type: "asset" });
    open(<ExportVisualsModal projectId={projectId} studyId={studyId} taskId={taskId} />);
  };

  const handleDataExport = () => {
    segmentTrackEvent("Study Exported", {
      projectId,
      studyId,
      exportType: "Data",
      studyType: "asset",
    });
    open(<ExportDataModal projectId={projectId} studyId={studyId} />);
  };

  const handleViewChange = (newView: string) => {
    // Segment tracking
    segmentTrackEvent("Study Report View Type Changed", {
      projectId,
      studyId,
      from: view,
      to: newView,
      studyType: "asset",
    });

    setView(newView as ViewType);
  };

  const handleEditStudy = () => {
    // Segment tracking
    segmentTrackEvent("Study Editing Initiated", {
      projectId,
      studyId,
      studyType: "asset",
    });

    // This modal will handle the navigation to the study review page
    open(<EditStudyModal projectId={projectId} studyId={studyId} />);
  };

  // report type header
  const getDisplayName = (tab: string) => {
    switch (tab) {
      case "creative":
        return "Creative Impact";
      case "attention":
        return "Attention";
      case "memory":
        return "Memory";
      case "emotion":
        return "Emotion";
      default:
        return tab.charAt(0).toUpperCase() + tab.slice(1);
    }
  };

  const currentTabDisplayName = getDisplayName(currentTab);

  // Get the appropriate info modal based on current tab
  const getInfoModal = () => {
    switch (currentTab) {
      case "creative":
        return <CreativeImpactInfoModal />;
      case "attention":
        return <AttentionScoreInfoModal />;
      case "memory":
        return <MemoryScoreInfoModal />;
      case "emotion":
        return <EmotionScoreInfoModal />;
      default:
        return null;
    }
  };

  const handleInfoModalClick = () => {
    // Segment tracking
    segmentTrackEvent("Study Report Tooltip Viewed", {
      name: currentTab,
      value: currentTabDisplayName,
      reportType: currentTab,
      studyId,
      projectId,
      studyType: "asset",
    });
  };

  /* Render */
  return (
    <>
      <Toolbar>
        <ToolbarItem>
          {/* Report Header */}
          <div className="flex items-center gap-2">
            <h1>{currentTabDisplayName}</h1>
            <InfoModalTrigger modalContent={getInfoModal()} onClick={handleInfoModalClick} />
          </div>
        </ToolbarItem>
        <ToolbarItem>
          <Button onClick={handleEditStudy}>
            <PencilSquareIcon className="size-3.5" />
            Edit study
          </Button>
          {isNewExportOptionsEnabled ? (
            <CustomDropdown
              align="end"
              triggerBtnClassname="p-0"
              triggerBtn={
                <Button>
                  <ArrowUpOnSquareIcon className="size-3.5" />
                  Export
                </Button>
              }
            >
              <CustomDropdownItem onClick={handleReportsExport} disabled={isCreatePowerPointPending}>
                <ComputerDesktopIcon className="size-4" />
                Presentation
              </CustomDropdownItem>
              <CustomDropdownItem onClick={handleVisualsExport} disabled={isCreateImagesPending}>
                <PhotoIcon className="size-4" />
                Visuals
              </CustomDropdownItem>
              <CustomDropdownItem onClick={handleDataExport}>
                <TableCellsIcon className="size-4" />
                Data (.csv)
              </CustomDropdownItem>
            </CustomDropdown>
          ) : (
            <Button onClick={handleReportsExport} disabled={isCreatePowerPointPending}>
              <ArrowUpOnSquareIcon className="size-3.5" />
              Export
            </Button>
          )}
        </ToolbarItem>
      </Toolbar>
      {showFilters && (
        <Toolbar>
          {/* View */}
          <ToolbarItem className="ml-auto">
            {rightContent || (
              <CustomDropdown
                align="end"
                triggerBtnClassname="p-0"
                triggerBtn={
                  <Button>
                    {view === "Cards" && <Squares2X2Icon className="size-3.5" />}
                    {view === "Chart" && <ChartBarIcon className="size-3.5" />}
                    {view === "List" && <Bars3Icon className="size-3.5" />}
                    {view}
                  </Button>
                }
              >
                <CustomDropdownItem onClick={() => handleViewChange("Cards")}>
                  <Squares2X2Icon className="size-4" />
                  Cards
                </CustomDropdownItem>
                <CustomDropdownItem onClick={() => handleViewChange("List")}>
                  <Bars3Icon className="size-4" />
                  List
                </CustomDropdownItem>
                <CustomDropdownItem onClick={() => handleViewChange("Chart")}>
                  <ChartBarIcon className="size-4" />
                  Charts
                </CustomDropdownItem>
              </CustomDropdown>
            )}
          </ToolbarItem>

          {/* Sort */}
          <ToolbarItem>
            <SelectSortReports currentTab={currentTab} orderBy={orderBy} sortBy={sortBy} setOrderBy={setOrderBy} setSortBy={setSortBy} />
          </ToolbarItem>
        </Toolbar>
      )}
    </>
  );
};
